/**
 * Reliable "Print / Save as PDF" helper.
 *
 * Why not window.print() on the current doc?
 *  - The print area sits inside a <Modal> that is `position: fixed` with its
 *    own scroll container + all sorts of ancestor backdrops. Hiding those via
 *    `visibility: hidden` reserves layout space, producing blank pages before
 *    the real content. Toggling `display: none` breaks the rest of the app.
 *
 * This helper sidesteps all of that by opening a new blank tab, injecting a
 * minimal HTML document that contains ONLY the print area + the print styles,
 * waiting for any images to load, and then calling `window.print()` inside
 * that tab. No blank pages, no layout reservations.
 */
export function printElement(elementId, { title = "Document", extraCss = "" } = {}) {
  const src = document.getElementById(elementId);
  if (!src) {
    // eslint-disable-next-line no-console
    console.warn("printElement: target missing", elementId);
    return;
  }

  // Pull existing <link rel="stylesheet"> + <style> so Tailwind/HV styles
  // apply in the new window. Skip any hot-reload helpers.
  const headStyles = Array.from(
    document.head.querySelectorAll('link[rel="stylesheet"], style')
  )
    .map((node) => node.outerHTML)
    .join("\n");

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) {
    alert("Popup blocker is preventing the print preview. Please allow popups.");
    return;
  }

  const doc = w.document;
  doc.open();
  // Wave 19 #100 — PRINT/PDF LIGHT-MODE CONTRACT
  // -------------------------------------------------------------------------
  // EVERY Print/PDF flow in this app MUST render the document in LIGHT MODE,
  // regardless of the user's current theme. Reasoning: PDFs are shared with
  // customers/vendors/clients who do not expect dark backgrounds, and dark
  // mode wastes ink + reduces contrast on real paper. This contract is
  // documented in /app/memory/CONTRACTS.md → "Print Light Mode Contract".
  //
  // We achieve this by:
  //   1. Setting `data-theme="light"` on the new <html>. This activates all
  //      `html[data-theme="light"] …` rules already defined in index.css.
  //   2. Pinning the `:root` CSS variables to their light-mode values inside
  //      the new doc with `!important` so any inline `style="color: var(...)"`
  //      computed against the dark theme is overridden.
  //   3. Forcing white background + dark text on html/body/card classes.
  //
  // Do NOT add a "use system theme" toggle for printing. The contract is:
  // "Always light, no exceptions, no opt-out."
  doc.write(`<!doctype html>
<html data-theme="light" lang="en">
<head>
<meta charset="utf-8" />
<title>${title.replace(/</g, "&lt;")}</title>
${headStyles}
<style>
  /* Wave 19 #100 — Print light-mode contract: pin CSS vars to light values
     so cards, buttons, chips render with white surfaces + dark text even
     when the source app is in dark mode. */
  :root, html, body {
    --hv-bg: #ffffff !important;
    --hv-surface: #ffffff !important;
    --hv-surface-2: #f4f6fb !important;
    --hv-border: #c5cad3 !important;
    --hv-text: #0a0b0e !important;
    --hv-text-secondary: #1c2230 !important;
    --hv-text-muted: #475063 !important;
    color-scheme: light !important;
  }
  /* Defeat any @media prefers-color-scheme dark the user-agent
     stylesheet may otherwise apply. */
  @media (prefers-color-scheme: dark) {
    :root, html, body {
      --hv-bg: #ffffff !important;
      --hv-surface: #ffffff !important;
      --hv-text: #0a0b0e !important;
      color-scheme: light !important;
    }
  }
  @page { margin: 14mm 12mm; size: A4; }
  html, body { margin: 0 !important; padding: 0 !important; background: #ffffff !important; color: #0a0b0e !important; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
  /* Force-light version of the app palette so we never print a dark card */
  .pcard { background: #ffffff !important; color: #0a0b0e !important; border-color: #c5cad3 !important; }
  .ptext { color: #0a0b0e !important; }
  .plabel { color: #0d6e75 !important; font-weight: 700 !important; }
  .paccent { color: #0d6e75 !important; }
  .pmeta  { color: #2b3140 !important; }
  .pdivider { border-color: #c5cad3 !important; }
  .pchip { color: #0a0b0e !important; background: transparent !important; border: 1px solid #c5cad3 !important; }
  /* Wave 19 #100 — defeat the dark-mode app classes. Any element that
     was rendered with a dark surface in the source app gets re-themed
     to white in print. */
  .hv-card, [class*="bg-[#0c1018]"], [class*="bg-[#121419]"], [class*="bg-[#0a0b0e]"] {
    background: #ffffff !important;
    color: #0a0b0e !important;
    border-color: #c5cad3 !important;
  }
  /* Common Tailwind dark surfaces used in the app (research brief, trip
     itinerary, job-card sections). */
  .bg-white\\/\\[0\\.04\\], .bg-white\\/\\[0\\.06\\], .bg-white\\/5, .bg-white\\/10,
  .bg-black\\/40, .bg-black\\/60, .bg-black\\/80 {
    background: #f4f6fb !important;
    color: #0a0b0e !important;
  }
  /* Muted greys we use for secondary text — bump to readable contrast. */
  .text-\\[\\#647087\\], .text-\\[\\#a0aabf\\], .text-\\[\\#cbd2df\\] {
    color: #2b3140 !important;
  }
  /* White text used on dark surfaces — invert to black. */
  .text-white { color: #0a0b0e !important; }
  .no-print { display: none !important; }
  .avoid-break, .avoid-break * { break-inside: avoid; page-break-inside: avoid; }
  /* Kill any fixed/sticky headers that may have leaked */
  * { position: static !important; }
  /* Images never overflow the page */
  img { max-width: 100% !important; height: auto !important; }
  ${extraCss}
</style>
</head>
<body>
${src.outerHTML}
</body>
</html>`);
  doc.close();

  const triggerPrint = () => {
    try {
      w.focus();
      w.print();
    } catch (e) {
      /* ignore */
    }
    // Close shortly after — Safari needs a tick before the print dialog is up.
    setTimeout(() => {
      try {
        w.close();
      } catch (e) {
        /* ignore */
      }
    }, 400);
  };

  // Wait for images to finish loading so they are rendered on page 1.
  const imgs = Array.from(doc.images || []);
  if (imgs.length === 0) {
    // Let the browser finish layout.
    setTimeout(triggerPrint, 100);
    return;
  }
  let remaining = imgs.length;
  const done = () => {
    remaining -= 1;
    if (remaining <= 0) setTimeout(triggerPrint, 100);
  };
  imgs.forEach((img) => {
    if (img.complete) {
      done();
    } else {
      img.addEventListener("load", done);
      img.addEventListener("error", done);
    }
  });
  // Safety net — print anyway after 4s in case something hangs.
  setTimeout(triggerPrint, 4000);
}
